import { Customer } from 'types/types';
import { isBrowserWindowAvailable } from './is-browser-window-available';

// stub queues calls until the real SDK loads, safe to call immediately
declare global {
  interface Window {
    // both set by the tail of the SDK snippet, see components/_global/bloomreach
    __bloomreachStarted?: boolean;
    brweb?: {
      identify: (
        id: string | Record<string, string>,
        properties?: Record<string, unknown>
      ) => void;
      anonymize: () => void;
      notifications: {
        isSubscribed: (
          callback: (isSubscribed: boolean, isDenied: boolean) => void
        ) => void;
        subscribe: (callback: (status: string) => void) => void;
      };
    };
  }
}

// dispatched on window by the tail of the SDK snippet, once brweb.start() has run
export const BLOOMREACH_STARTED_EVENT = 'bloomreach:started';

// the SDK snippet mounts via next/script, so it may not have run yet when a component effect fires.
// start() queues initialize() on the stub, so anything we queue from here is replayed behind it -
// no need to wait for brweb.min.js itself. the flag covers the snippet having run before we listened,
// and keeps this working across remounts, when next/script will not re-append (and so not re-dispatch).
// every window.brweb call in this file goes through here - calling before the SDK has started
// silently no-ops via optional chaining, with no retry, so nothing gets to skip this guard
const onSDKLoaded = (callback: () => void): void => {
  if (window.__bloomreachStarted) {
    callback();
    return;
  }

  window.addEventListener(BLOOMREACH_STARTED_EVENT, () => callback(), {
    once: true,
  });
};

// @see https://documentation.bloomreach.com/engagement/docs/tracking#identify
export const identifyCustomer = (customer?: Customer): void => {
  if (!isBrowserWindowAvailable() || !customer?.email) return;

  onSDKLoaded(() => {
    try {
      window.brweb?.identify(
        { registered: customer.email },
        {
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
        }
      );
    } catch (error) {
      console.error(`Bloomreach - identify: ${error}`);
    }
  });
};

// @see https://documentation.bloomreach.com/engagement/docs/tracking#anonymize
export const anonymizeCustomer = (): void => {
  if (!isBrowserWindowAvailable()) return;

  onSDKLoaded(() => {
    try {
      window.brweb?.anonymize();
    } catch (error) {
      console.error(`Bloomreach - anonymize: ${error}`);
    }
  });
};

// migrates users already granting browser permission (e.g. via Frizbit) to Bloomreach, without re-prompting
// @see https://documentation.bloomreach.com/engagement/docs/browser-push-notifications
export const autoSubscribeToPushNotifications = (): void => {
  if (!isBrowserWindowAvailable() || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  onSDKLoaded(() => {
    try {
      window.brweb?.notifications.isSubscribed((isSubscribed, isDenied) => {
        if (isSubscribed || isDenied) return;

        // status isn't actionable here, but subscribe() requires a callback
        window.brweb?.notifications.subscribe(() => undefined);
      });
    } catch (error) {
      console.error(`Bloomreach - auto subscribe: ${error}`);
    }
  });
};
